/* Closer Cards: "Your Own Design" product page (Oct 30 2026). Loaded only on /products/your-own-design.
   The customer designs the whole card front in our Canva template, downloads it and uploads it here.
   Rules (CC Graphic Design spec, final 30 Oct 2026): PNG/JPG (HEIC if the browser can open it), up to 40 MB,
   EXIF rotation applied, transparency on white, 5:7 portrait: within 1 % scaled, 1-15 % off centre-cropped after the
   customer confirms, more or landscape rejected; at least 1050 x 1470 px after crop (under 1500 wide: soft warning);
   leftover template guide lines rejected (port of detect_guides.py).
   What goes on the order (Shopify line-item file upload, stored by Shopify, free): properties[Design] = the checked
   design as PNG (<= 1500 x 2100, exact 5:7; JPEG q0.95 only if the PNG were over the limit),
   properties[_Design original] = the customer's own file when it is 20 MB or less (dropped automatically if refused),
   properties[_Design check] = what was checked. The print pipeline re-checks and scales to 1500 x 2100. */
(function () {
  'use strict';
  var W = 1500, H = 2100, MIN_W = 1050, MIN_H = 1470, MAX_BYTES = 40 * 1024 * 1024, ORIG_MAX = 20 * 1024 * 1024;
  var SCALE_TOL = 0.01, CROP_TOL = 0.15, MAX_CANVAS_PX = 16000000;
  var MSG = {
    tooLarge: 'Please choose a file under 40 MB.',
    tooSmall: 'This file is too small to print well. Please upload a design at least 1050 x 1470 px.',
    shape: 'Please upload a vertical (portrait) design in a 5 x 7 shape.',
    lowRes: 'Your design will print a little soft. For the sharpest card, upload a larger file (1500 x 2100 px).',
    crop: 'We\u2019ve centred your design to fit a 5 x 7 card. Check the preview and confirm, or upload a 5 x 7 version.',
    guides: 'Please delete the guide lines before downloading and upload again',
    type: 'Please upload your design as a PNG or JPG file.',
    unreadable: 'We couldn\u2019t open this file. Please download your design as a PNG and upload it again.',
    cmyk: 'This file uses print colours, so the colours may shift slightly. For the truest colours, download it as a PNG.',
    missing: 'Add your card design (PNG or JPG).',
    confirm: 'Please confirm the crop, or upload a 5 x 7 version.',
    busy: 'Checking your design\u2026 one moment.',
    ok: 'Looks good. Tap to choose a different design.'
  };

  /* ---------- leftover template guide lines (JS port of detect_guides.py) ----------
     Input: RGBA pixels of the design resampled to 1500 x 2100. Same thresholds as the Python check.
     Limits vs. the Python version: the browser resamples with its own (bilinear-type) filter instead of Lanczos, and
     colour management depends on the browser, so values near a threshold can differ slightly. The print pipeline runs
     the Python check again, so a miss here is still caught (as an order to fix by hand, not at upload). */
  var GREEN = [47, 74, 60], GOLD = [196, 163, 90];
  function near(r, g, b, c, tol, green) {
    if (Math.max(Math.abs(r - c[0]), Math.abs(g - c[1]), Math.abs(b - c[2])) >= tol) return false;
    if (green && !((g - r) >= 15 && (g - b) >= 5)) return false;
    return true;
  }
  function sideLine(px, side, onFrom, onTo, offFrom, offTo, col, tol, green) {
    /* side: 0 top, 1 bottom, 2 left, 3 right; row 0 = outer edge; columns run along the side, corners trimmed by 80 */
    var len = (side < 2 ? W : H) - 160, out = new Uint8Array(len);
    function at(row, k) {
      var x, y;
      if (side === 0) { x = 80 + k; y = row; } else if (side === 1) { x = 80 + k; y = H - 1 - row; }
      else if (side === 2) { x = row; y = 80 + k; } else { x = W - 1 - row; y = 80 + k; }
      return (y * W + x) * 4;
    }
    for (var k = 0; k < len; k++) {
      var on = false, r, i;
      for (r = onFrom; r < onTo && !on; r++) { i = at(r, k); on = near(px[i], px[i + 1], px[i + 2], col, tol, green); }
      if (!on) continue;
      var sr = 0, sg = 0, sb = 0, n = offTo - offFrom;
      for (r = offFrom; r < offTo; r++) { i = at(r, k); sr += px[i]; sg += px[i + 1]; sb += px[i + 2]; }
      out[k] = near(sr / n, sg / n, sb / n, col, 60, green) ? 0 : 1;
    }
    return out;
  }
  function mean(a) { var s = 0; for (var i = 0; i < a.length; i++) s += a[i]; return a.length ? s / a.length : 0; }
  function periodStrength(sig) {
    var m = mean(sig), n = sig.length, s = new Float64Array(n), den = 0, best = -Infinity, i, l;
    for (i = 0; i < n; i++) { s[i] = sig[i] - m; den += s[i] * s[i]; }
    if (den === 0) return 0;
    for (l = 33; l <= 37; l++) { var t = 0; for (i = 0; i + l < n; i++) t += s[i] * s[i + l]; best = Math.max(best, t / den); }
    return best;
  }
  function checkGuides(px) {
    var trim = [], safe = [], per = [], s;
    for (s = 0; s < 4; s++) {
      trim.push(mean(sideLine(px, s, 0, 6, 14, 20, GREEN, 35, true)));
      var sig = sideLine(px, s, 32, 44, 52, 58, GOLD, 45, false);
      safe.push(mean(sig)); per.push(periodStrength(sig));
    }
    var trimLeft = trim.filter(function (f) { return f >= 0.60; }).length >= 3;
    var safeHits = 0; for (s = 0; s < 4; s++) if (safe[s] >= 0.30 && safe[s] <= 0.90 && per[s] >= 0.30) safeHits++;
    var reject = trimLeft || safeHits >= 3;
    return { ok: !reject, reason: [trimLeft ? 'card-edge guide line found' : '', safeHits >= 3 ? 'safe-margin guide line found' : ''].filter(Boolean).join(', ') || 'no guide lines',
      message: reject ? MSG.guides : '', metrics: { trim_sides: trim, safe_sides: safe, safe_period: per, safe_sides_hit: safeHits } };
  }

  /* ---------- shape ---------- */
  function aspectOff(w, h) { return Math.abs((w / h) / (5 / 7) - 1); }
  function cropRect(w, h) {
    if (w / h > 5 / 7) { var nw = Math.round(h * 5 / 7), x0 = Math.floor((w - nw) / 2); return { x: x0, y: 0, w: nw, h: h }; }
    var nh = Math.round(w * 7 / 5), y0 = Math.floor((h - nh) / 2); return { x: 0, y: y0, w: w, h: nh };
  }
  /* Decide what happens to a w x h image: {action: 'reject'|'crop'|'scale', code, rect} */
  function plan(w, h) {
    var off = aspectOff(w, h);
    if (w > h || off > CROP_TOL) return { action: 'reject', code: 'shape', off: off };
    var rect = off > SCALE_TOL ? cropRect(w, h) : { x: 0, y: 0, w: w, h: h };
    if (rect.w < MIN_W || rect.h < MIN_H) return { action: 'reject', code: 'tooSmall', off: off, rect: rect };
    return { action: off > SCALE_TOL ? 'crop' : 'scale', off: off, rect: rect, lowRes: rect.w < W };
  }

  /* ---------- image helpers ---------- */
  function canvas(w, h) { var c = document.createElement('canvas'); c.width = w; c.height = h; return c; }
  function decode(file) {
    var tryBitmap = window.createImageBitmap
      ? createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () { return createImageBitmap(file); })
      : Promise.reject(new Error('no createImageBitmap'));
    return tryBitmap.catch(function () {
      return new Promise(function (res, rej) {
        var url = URL.createObjectURL(file), img = new Image();
        img.onload = function () { res(img); };
        img.onerror = function () { URL.revokeObjectURL(url); rej(new Error('decode')); };
        img.src = url;
      });
    });
  }
  function dims(src) { return { w: src.naturalWidth || src.width, h: src.naturalHeight || src.height }; }
  /* Draw rect of src into an exact ow x oh canvas on white, stepping down by halves for large reductions
     (each intermediate canvas kept under ~16 MP for phones). */
  function render(src, rect, ow, oh) {
    var cur = src, cr = rect, steps = [], sw = ow, sh = oh;
    while (sw * 2 < rect.w && (sw * 2) * (sh * 2) <= MAX_CANVAS_PX) { sw *= 2; sh *= 2; steps.unshift([sw, sh]); }
    steps.push([ow, oh]);
    for (var i = 0; i < steps.length; i++) {
      var c = canvas(steps[i][0], steps[i][1]), x = c.getContext('2d');
      x.fillStyle = '#ffffff'; x.fillRect(0, 0, c.width, c.height);
      x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
      x.drawImage(cur, cr.x, cr.y, cr.w, cr.h, 0, 0, c.width, c.height);
      cur = c; cr = { x: 0, y: 0, w: c.width, h: c.height };
    }
    return cur;
  }
  function toBlob(c, type, q) { return new Promise(function (res) { c.toBlob(res, type, q); }); }
  function isCmykJpeg(file) {
    if (!/jpe?g$/i.test(file.type || '') && !/\.jpe?g$/i.test(file.name || '')) return Promise.resolve(false);
    return file.slice(0, 262144).arrayBuffer().then(function (buf) {
      var b = new Uint8Array(buf), i = 2;
      while (i + 9 < b.length) {
        if (b[i] !== 0xFF) { i++; continue; }
        var m = b[i + 1];
        if (m >= 0xC0 && m <= 0xCF && m !== 0xC4 && m !== 0xC8 && m !== 0xCC) return b[i + 9] === 4;
        if (m === 0xD8 || (m >= 0xD0 && m <= 0xD7) || m === 0x01) { i += 2; continue; }
        i += 2 + ((b[i + 2] << 8) | b[i + 3]);
      }
      return false;
    }).catch(function () { return false; });
  }
  var OK_TYPE = /^(image\/(png|jpe?g|heic|heif))$/i, OK_EXT = /\.(png|jpe?g|heic|heif)$/i;

  /* Full check of one file -> Promise of {ok, error?, warn[], needsConfirm, blob, previewUrl, check, rect, src, out} */
  function analyse(file) {
    if (file.size > MAX_BYTES) return Promise.resolve({ ok: false, error: MSG.tooLarge });
    if (!OK_TYPE.test(file.type || '') && !OK_EXT.test(file.name || '')) return Promise.resolve({ ok: false, error: MSG.type });
    return decode(file).then(function (src) {
      var d = dims(src), p = plan(d.w, d.h), warn = [];
      if (p.action === 'reject') return { ok: false, error: MSG[p.code] };
      var ow = Math.min(W, p.rect.w), oh = ow === W ? H : Math.round(ow * 7 / 5);
      var out = render(src, p.rect, ow, oh);
      var g = checkGuides((ow === W ? out : render(out, { x: 0, y: 0, w: ow, h: oh }, W, H)).getContext('2d').getImageData(0, 0, W, H).data);
      if (!g.ok) return { ok: false, error: MSG.guides, guides: g };
      if (p.lowRes) warn.push(MSG.lowRes);
      return isCmykJpeg(file).then(function (cmyk) {
        if (cmyk) warn.push(MSG.cmyk);
        return toBlob(out, 'image/png').then(function (b) {
          if (!b || b.size > ORIG_MAX - 512 * 1024) return toBlob(out, 'image/jpeg', 0.95).then(function (j) { return { b: j, ext: 'jpg' }; });
          return { b: b, ext: 'png' };
        }).then(function (o) {
          if (src.close) try { src.close(); } catch (e) {}
          var check = 'src=' + d.w + 'x' + d.h + ';off=' + (p.off * 100).toFixed(1) + '%;' +
            (p.action === 'crop' ? 'crop=' + [p.rect.x, p.rect.y, p.rect.w, p.rect.h].join(',') : 'crop=none') +
            ';out=' + ow + 'x' + oh + ';guides=pass' + (p.lowRes ? ';lowres' : '') + (cmyk ? ';cmyk' : '') + ';v=1';
          return { ok: true, warn: warn, needsConfirm: p.action === 'crop', blob: o.b, ext: o.ext, previewUrl: URL.createObjectURL(o.b),
            check: check, guides: g, src: d, out: { w: ow, h: oh }, rect: p.rect };
        });
      });
    }, function () { return { ok: false, error: MSG.unreadable }; });
  }

  window.CloserCards = window.CloserCards || {};
  window.CloserCards.ownDesign = { checkGuides: checkGuides, aspectOff: aspectOff, cropRect: cropRect, plan: plan, analyse: analyse, MSG: MSG };

  /* ---------- page wiring ---------- */
  function init() {
    var input = document.querySelector('[data-od-file]'); if (!input) return;
    var form = input.form || input.closest('form'); if (!form) return;
    var zone = form.querySelector('[data-drop-zone="design"]');
    var errEl = form.querySelector('[data-od-error]'), warnEl = form.querySelector('[data-od-warn]');
    var cropBox = form.querySelector('[data-od-crop]'), checkInput = form.querySelector('[data-od-check]');
    var thumb = zone && zone.querySelector('[data-photo-thumb]'), nameEl = zone && zone.querySelector('[data-od-name]'), hintEl = zone && zone.querySelector('[data-od-hint]');
    var stage = document.querySelector('[data-od-stage]');
    var cardImg = stage && stage.querySelector('[data-od-design]'), emptyEl = stage && stage.querySelector('[data-od-empty]');
    var guidesImg = stage && stage.querySelector('[data-od-guides]'), guidesToggle = stage && stage.querySelector('[data-od-guides-toggle]');
    var vidBtn = stage && stage.querySelector('[data-od-play]'), cardVid = stage && stage.querySelector('[data-od-video]');
    var state = { status: 'empty', res: null, file: null, token: 0 };

    function show(el, text) { if (!el) return; el.textContent = text || ''; el.hidden = !text; }
    function setFilled(on) {
      if (!zone) return;
      zone.classList.toggle('has-file', !!on);
      var e = zone.querySelector('[data-drop-empty]'), f = zone.querySelector('[data-drop-filled]');
      if (e) e.hidden = !!on; if (f) f.hidden = !on;
    }
    function setPreview(url) {
      if (!cardImg) return;
      if (url) { cardImg.src = url; cardImg.hidden = false; if (emptyEl) emptyEl.hidden = true; }
      else { cardImg.removeAttribute('src'); cardImg.hidden = true; if (emptyEl) emptyEl.hidden = false; }
    }
    function reset(msg) {
      state.status = 'empty'; state.res = null; state.file = null;
      input.value = ''; setFilled(false); setPreview(null); show(warnEl, ''); if (cropBox) cropBox.hidden = true;
      if (checkInput) checkInput.value = '';
      show(errEl, msg || '');
    }
    function handle(file) {
      if (!file) return;
      var tok = ++state.token;
      state.status = 'busy'; state.file = file; show(errEl, ''); show(warnEl, ''); if (cropBox) cropBox.hidden = true;
      setFilled(true); if (nameEl) nameEl.textContent = file.name || 'Your design'; if (hintEl) hintEl.textContent = MSG.busy;
      if (thumb) thumb.removeAttribute('src');
      analyse(file).then(function (r) {
        if (tok !== state.token) return;
        if (!r.ok) { reset(r.error); return; }
        state.res = r; state.status = r.needsConfirm ? 'confirm' : 'ready';
        if (thumb) thumb.src = r.previewUrl;
        setPreview(r.previewUrl);
        if (guidesToggle && guidesToggle.checked && r.needsConfirm === false) { /* keep the customer's choice */ }
        if (checkInput) checkInput.value = r.check + (r.needsConfirm ? ';confirmed=no' : '');
        show(warnEl, r.warn.join(' '));
        if (cropBox) cropBox.hidden = !r.needsConfirm;
        if (hintEl) hintEl.textContent = r.needsConfirm ? MSG.confirm : MSG.ok;
        if (r.needsConfirm && stage) try { stage.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
      }, function () { if (tok === state.token) reset(MSG.unreadable); });
    }
    input.addEventListener('change', function () { handle(input.files && input.files[0]); });
    if (zone) {
      zone.addEventListener('click', function (e) { if (e.target === input) return; input.click(); });
      zone.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); } });
      ['dragenter', 'dragover'].forEach(function (t) { zone.addEventListener(t, function (e) { e.preventDefault(); zone.classList.add('is-drag'); }); });
      ['dragleave', 'drop'].forEach(function (t) { zone.addEventListener(t, function (e) { e.preventDefault(); zone.classList.remove('is-drag'); }); });
      zone.addEventListener('drop', function (e) {
        var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]; if (!f) return;
        try { var dt = new DataTransfer(); dt.items.add(f); input.files = dt.files; } catch (err) {}
        handle(f);
      });
    }
    if (cropBox) {
      var okBtn = cropBox.querySelector('[data-od-crop-ok]'), otherBtn = cropBox.querySelector('[data-od-crop-other]');
      if (okBtn) okBtn.addEventListener('click', function () {
        if (state.status !== 'confirm') return;
        state.status = 'ready'; cropBox.hidden = true; if (hintEl) hintEl.textContent = MSG.ok; show(errEl, '');
        if (checkInput) checkInput.value = state.res.check + ';confirmed=yes';
      });
      if (otherBtn) otherBtn.addEventListener('click', function () { reset(''); input.click(); });
    }
    if (guidesToggle && guidesImg) {
      var syncG = function () { guidesImg.hidden = !guidesToggle.checked; };
      guidesToggle.addEventListener('change', syncG); syncG();
    }
    /* show the chosen video playing in the card's video area */
    var vInput = form.querySelector('[data-card-video]'), vUrl = null;
    if (vInput && cardVid && vidBtn) {
      vInput.addEventListener('change', function () {
        var f = vInput.files && vInput.files[0];
        if (vUrl) { URL.revokeObjectURL(vUrl); vUrl = null; }
        cardVid.pause(); cardVid.hidden = true; vidBtn.setAttribute('aria-pressed', 'false');
        if (!f) { vidBtn.hidden = true; return; }
        vUrl = URL.createObjectURL(f); cardVid.src = vUrl; vidBtn.hidden = false; vidBtn.textContent = 'Play your video on the card';
      });
      vidBtn.addEventListener('click', function () {
        var on = cardVid.hidden;
        cardVid.hidden = !on; vidBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        vidBtn.textContent = on ? 'Stop the video' : 'Play your video on the card';
        if (on) { var pr = cardVid.play(); if (pr && pr.catch) pr.catch(function () {}); } else cardVid.pause();
      });
    }
    /* block add to cart until the design is checked (runs before the theme's own checks) */
    form.addEventListener('submit', function (e) {
      var msg = state.status === 'empty' ? MSG.missing : state.status === 'busy' ? MSG.busy : state.status === 'confirm' ? MSG.confirm : '';
      if (!msg) return;
      e.preventDefault(); e.stopImmediatePropagation();
      show(errEl, msg);
      var t = (zone && zone.closest('.create-field')) || zone;
      if (t) try { t.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (err) {}
    }, true);
    /* attach the checked design to the add-to-cart request (theme.js calls this before posting) */
    form._ccBeforeAdd = function (fd) {
      var r = state.res; if (!r || state.status !== 'ready') return { fd: fd };
      var stem = String(state.file && state.file.name || 'design').replace(/\.[a-z0-9]+$/i, '').replace(/[^\w\- ]+/g, '').slice(0, 60) || 'design';
      fd.set('properties[Design]', r.blob, stem + '-design.' + r.ext);
      if (checkInput && checkInput.value) fd.set('properties[_Design check]', checkInput.value);
      var fallback = new FormData();
      fd.forEach(function (v, k) { if (typeof v === 'string') fallback.append(k, v); else fallback.append(k, v, v.name || 'file'); });
      if (state.file && state.file.size <= ORIG_MAX) fd.set('properties[_Design original]', state.file, state.file.name || 'original');
      return { fd: fd, fallback: fallback };
    };
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
